import { mergeSx } from "@/utils/styleHelpers";
import { ClassNames, type DistributiveOmit } from "@emotion/react";
import { type ReactElement, useMemo } from "react";

import { Button } from "@/components/Clickable";
import { BUTTON_SIZES, DEFAULT_BUTTON_SIZE } from "@/constants";
import { useTheme } from "@/hooks";
import type { ButtonProps } from "@/types";
import { getStartingSize } from "@/utils/styleHelpers";

import {
  buttonBaseSxProps,
  getButtonStyles,
  renderResponsiveButtonStyles,
} from "./styles";

export type HorizontalMenuButtonProps<
  RC extends ReactElement | undefined = undefined,
> = DistributiveOmit<ButtonProps<RC>, "variant"> & {
  selected?: boolean;
};

export function HorizontalMenuButton<
  RC extends ReactElement | undefined = undefined,
>({
  children,
  sx,
  size = "medium",
  selected,
  className,
  ...props
}: HorizontalMenuButtonProps<RC>) {
  const theme = useTheme();
  const startingSize = getStartingSize(size, DEFAULT_BUTTON_SIZE, BUTTON_SIZES);

  const mergedSx = useMemo(
    () =>
      mergeSx(
        buttonBaseSxProps,
        getButtonStyles({ size: startingSize, theme }),
        renderResponsiveButtonStyles({
          size,
          theme,
        }),
        sx,
      ),
    [sx, startingSize, size, theme],
  );

  return (
    <ClassNames>
      {({ cx }) => (
        <Button
          {...props}
          size={size}
          variant="tertiary"
          className={cx(className, "HorizontalMenuButton", { selected })}
          sx={mergedSx}
        >
          {children}
        </Button>
      )}
    </ClassNames>
  );
}

HorizontalMenuButton.displayName = "HorizontalMenuButton";
HorizontalMenuButton.Icon = Button.Icon;
HorizontalMenuButton.Logo = Button.Logo;
HorizontalMenuButton.FramedImage = Button.FramedImage;
